import React, { useState, useMemo, useEffect, useRef } from 'react'
import { PURPOSES, TRADES, tradeOrder, templatesForTrade } from '../data/templates'
import { templateTrade } from '../data/userTemplates'
import { LOOKS, LOOK_ORDER, resolveTheme } from '../email/theme'
import { renderEmail, isThemedEmail } from '../email/render'
import { buildTemplateBlocks, templateSpec, designedLook, BRAND_LOOK } from '../email/apply'
import { EmailFrame, EmailThumb } from './EmailHtml'
import { BlockRenderer } from './BlockRenderer'
import { hapticTick } from '../../../../../_utils/haptics'

// Step one of the email creator: the template library. Everything is shown,
// laid out by trade with the salon's own first, then the templates for every
// business, then the other trades (a hair salon may love a barber's design).
// Templates show as they were designed, so the range is obvious, or all in
// the salon's brand with one tap. Previews are the real email HTML.

const IcBack = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M15 18l-6-6 6-6" /></svg>)
const IcDesktop = () => (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden><rect x="2" y="3" width="20" height="14" rx="2" /><line x1="8" y1="21" x2="16" y2="21" /><line x1="12" y1="17" x2="12" y2="21" /></svg>)
const IcPhone = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden><rect x="5" y="2" width="14" height="20" rx="2.5" /><line x1="11" y1="18" x2="13" y2="18" /></svg>)
const IcPlus = () => (<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden><path d="M12 5v14M5 12h14" /></svg>)
const IcMore = () => (<svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden><circle cx="5" cy="12" r="1.8" /><circle cx="12" cy="12" r="1.8" /><circle cx="19" cy="12" r="1.8" /></svg>)
const IcCheck = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>)

// Columns follow the grid in EmailBuilder.css: 4 on large screens, 3 on
// laptops, 2 below that (phones included)
const columnsNow = () => {
  if (typeof window === 'undefined' || !window.matchMedia) return 3
  if (window.matchMedia('(min-width: 1500px)').matches) return 4
  if (window.matchMedia('(min-width: 1280px)').matches) return 3
  return 2
}
const useColumns = () => {
  const [cols, setCols] = useState(columnsNow)
  useEffect(() => {
    const on = () => setCols(columnsNow())
    window.addEventListener('resize', on)
    return () => window.removeEventListener('resize', on)
  }, [])
  return cols
}

// "As designed" or "In your brand", remembered on this device
const MODE_KEY = 'beu-template-library-view'
const readMode = () => {
  try { return localStorage.getItem(MODE_KEY) === 'brand' ? 'brand' : 'designed' } catch (e) { return 'designed' }
}
const writeMode = (m) => {
  try { localStorage.setItem(MODE_KEY, m) } catch (e) { /* private mode: lasts the visit */ }
}

const purposeLabel = (id) => (PURPOSES.find(p => p.id === id) || {}).label || ''
const tradeLabel = (id) => (TRADES[id] || TRADES.all).label

// A saved design (your templates, drafts): new-style emails preview as real
// HTML; classic ones keep their classic preview.
const SavedThumb = ({ blocks, sample, height }) => {
  const themed = isThemedEmail(blocks)
  const html = useMemo(() => (themed ? renderEmail(blocks, { sample }) : ''), [blocks, themed, sample])
  if (!blocks || !blocks.length) return <div className="tp-thumb-empty" style={{ height }}>No design yet</div>
  if (themed) return <EmailThumb html={html} height={height} />
  return (
    <div style={{ height, overflow: 'hidden', pointerEvents: 'none', background: '#fff' }}>
      <div style={{ transform: 'scale(0.5)', transformOrigin: 'top left', width: '200%' }}>
        {blocks.slice(0, 4).map((block, idx) => <BlockRenderer key={idx} block={{ ...block, id: `saved-prev-${idx}` }} />)}
      </div>
    </div>
  )
}

// A look's swatch: its page colour with its loudest colour in the middle
const LookDot = ({ id, className = 'tp-look-dot' }) => {
  const p = resolveTheme({ look: id }).palette
  return (
    <span className={className} style={{ background: p.canvas }}>
      <span style={{ background: p.pop || p.accent }} />
    </span>
  )
}

// A look to try, in its own colours
const LookChip = ({ id, active, original, onPick }) => (
  <button type="button" className={`tp-look ${active ? 'is-on' : ''}`} onClick={() => onPick(id)} aria-pressed={active}>
    <LookDot id={id} />
    {LOOKS[id].name}
    {original && <span className="tp-look-tag">Original</span>}
  </button>
)

// The salon's own brand, always first
const BrandChip = ({ theme, active, onPick }) => (
  <button type="button" className={`tp-look tp-look--brand ${active ? 'is-on' : ''}`} onClick={() => onPick(BRAND_LOOK)} aria-pressed={active}>
    {theme.logo
      ? <span className="tp-look-logo" style={{ background: theme.palette.paper }}><img src={theme.logo} alt="" /></span>
      : <span className="tp-look-dot" style={{ background: theme.palette.canvas }}><span style={{ background: theme.palette.accent }} /></span>}
    Your brand
  </button>
)

// The preview opens as the library showed it, so the design that was
// clicked is the one that appears; "Your brand" stays first, one tap away.
const TemplatePreview = ({ tpl, market, kit, links, sample, isMobile, initialLook, onClose, onUse }) => {
  const original = designedLook(tpl, market)
  const [look, setLook] = useState(initialLook || BRAND_LOOK)
  const brandTheme = useMemo(() => resolveTheme(templateSpec(tpl, { market, kit, look: BRAND_LOOK })), [tpl, market, kit])
  const shownTheme = look === BRAND_LOOK ? brandTheme : resolveTheme({ look })
  const [device, setDevice] = useState(isMobile ? 'mobile' : 'desktop')
  const html = useMemo(() => renderEmail(buildTemplateBlocks(tpl, { market, kit, look, links }), { subject: tpl.subject, sample }), [tpl, market, kit, look, links, sample])
  const subject = String(tpl.subject || '').replace(/\{\{\s*FirstName\s*\}\}/g, sample.FirstName || 'Sophie').replace(/\{\{\s*SalonName\s*\}\}/g, sample.SalonName || 'Your salon')
  const preheader = String(tpl.preheader || '').replace(/\{\{\s*FirstName\s*\}\}/g, sample.FirstName || 'Sophie').replace(/\{\{\s*SalonName\s*\}\}/g, sample.SalonName || 'Your salon')
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])
  let hint = 'Your logo, colour and type. Change your brand any time in Brand.'
  if (look !== BRAND_LOOK) {
    hint = look === original
      ? `${LOOKS[look].blurb}. The look it was designed in. It styles this email only; your brand stays as it is.`
      : `${LOOKS[look].blurb}. For this email only; your brand stays as it is.`
  }
  return (
    <div className="tp-preview" role="dialog" aria-modal="true" aria-label={`${tpl.name} preview`}>
      <div className="tp-preview-bar">
        <button type="button" className="tp-back" onClick={onClose}><IcBack /> All templates</button>
        {!isMobile && (
          <div className="tp-device" role="group" aria-label="Preview size">
            <button type="button" className={device === 'desktop' ? 'is-on' : ''} onClick={() => setDevice('desktop')} aria-label="Desktop preview"><IcDesktop /></button>
            <button type="button" className={device === 'mobile' ? 'is-on' : ''} onClick={() => setDevice('mobile')} aria-label="Phone preview"><IcPhone /></button>
          </div>
        )}
      </div>
      <div className="tp-preview-body">
        <div className="tp-preview-stage" style={{ background: shownTheme.palette.canvas }}>
          <div className="tp-inbox" style={{ maxWidth: device === 'mobile' ? 375 : 680 }}>
            <p className="tp-inbox-from">{sample.SalonName || 'Your salon'}</p>
            <p className="tp-inbox-subject">{subject}</p>
            <p className="tp-inbox-pre">{preheader}</p>
          </div>
          <div className={`tp-frame ${device === 'mobile' ? 'is-phone' : ''}`}>
            <EmailFrame key={device} html={html} width={device === 'mobile' ? 375 : 680} title={`${tpl.name} email`} />
          </div>
        </div>
        <aside className="tp-preview-info">
          <p className="tp-kicker">{tradeLabel(tpl.market)} · {purposeLabel(tpl.purpose)}</p>
          <h2 className="tp-title">{tpl.name}</h2>
          <p className="tp-desc">{tpl.description}</p>
          <p className="tp-label">Look</p>
          <div className="tp-looks">
            <BrandChip theme={brandTheme} active={look === BRAND_LOOK} onPick={setLook} />
            {LOOK_ORDER.map(id => <LookChip key={id} id={id} active={look === id} original={id === original} onPick={setLook} />)}
          </div>
          <p className="tp-hint">{hint}</p>
          <div className="tp-actions">
            <button type="button" className="tp-btn tp-btn--ghost" onClick={onClose}>Back</button>
            <button type="button" className="tp-btn" onClick={() => onUse(tpl, look)}>Use this template</button>
          </div>
        </aside>
      </div>
    </div>
  )
}

// A saved template's menu: which trade it shows under, or delete it
const SavedMenu = ({ item, trade, onSetTrade, onDelete }) => {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)
  useEffect(() => {
    if (!open) return undefined
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); setOpen(false) } }
    document.addEventListener('pointerdown', onDown, true)
    window.addEventListener('keydown', onKey, true)
    return () => {
      document.removeEventListener('pointerdown', onDown, true)
      window.removeEventListener('keydown', onKey, true)
    }
  }, [open])
  return (
    <div className="tp-card-menu-wrap" ref={ref}>
      <button type="button" className="tp-card-menu-btn" aria-label={`Options for ${item.name}`} aria-expanded={open} onClick={() => { hapticTick(); setOpen(o => !o) }}><IcMore /></button>
      {open && (
        <div className="tp-card-menu" role="menu">
          <p className="tp-card-menu-label">Show it under</p>
          {['hair', 'barber', 'beauty', 'all'].map(id => (
            <button key={id} type="button" role="menuitemradio" aria-checked={trade === id} onClick={() => { setOpen(false); if (trade !== id) onSetTrade(item, id) }}>
              {tradeLabel(id)}{trade === id && <IcCheck />}
            </button>
          ))}
          <hr />
          <button type="button" role="menuitem" className="is-danger" onClick={() => { setOpen(false); onDelete(item.id) }}>Delete template</button>
        </div>
      )}
    </div>
  )
}

export const TemplatePicker = ({
  market, kit, links, sample, isMobile, isDesignMode,
  userTemplates, drafts, onDeleteUserTemplate, onSetTemplateTrade, onOpenSaved, onOpenDraft, onUseTemplate, onBlank,
}) => {
  const [view, setView] = useState('everything')
  const [mode, setModeState] = useState(readMode)
  const [previewing, setPreviewing] = useState(null)
  const cols = useColumns()
  const thumbH = isMobile ? 250 : cols === 4 ? 360 : cols === 3 ? 400 : 340
  const own = market || null
  const order = tradeOrder(own)

  const setMode = (m) => {
    if (m === mode) return
    hapticTick()
    setModeState(m)
    writeMode(m)
  }

  const lookFor = (tpl) => (mode === 'brand' ? BRAND_LOOK : designedLook(tpl, own))
  const htmlFor = useMemo(() => {
    const cache = {}
    return (tpl) => {
      const key = `${mode}:${tpl.id}`
      if (!cache[key]) {
        const look = mode === 'brand' ? BRAND_LOOK : designedLook(tpl, own)
        cache[key] = renderEmail(buildTemplateBlocks(tpl, { market: own || 'hair', kit, links, look }), { subject: tpl.subject, sample })
      }
      return cache[key]
    }
  }, [mode, own, kit, links, sample])

  // A saved template without a trade belongs to the salon's own
  const tradeOfSaved = (t) => templateTrade(t, own || 'all')
  const savedFor = (trade) => userTemplates.filter(t => tradeOfSaved(t) === trade)

  const views = [
    { id: 'everything', label: 'All templates' },
    ...order.map(id => ({ id, label: TRADES[id].label, yours: id === own })),
  ]
  const yours = [
    ...(userTemplates.length ? [{ id: 'mine', label: 'Your templates', count: userTemplates.length }] : []),
    ...(!isDesignMode && drafts.length ? [{ id: 'drafts', label: 'Drafts', count: drafts.length }] : []),
  ]
  const showLibrary = view !== 'mine' && view !== 'drafts'
  const sections = view === 'everything' ? order : (showLibrary ? [view] : [])
  const brandSwatch = kit && kit.accent

  const blankCard = (
    <div className="tp-card" key="blank">
      <button type="button" className="tp-card-hit" onClick={() => onBlank()}>
        <div className="tp-thumb tp-thumb--blank" style={{ height: thumbH }}>
          <span className="tp-blank-plus"><IcPlus /></span>
          <span className="tp-blank-title">Start from scratch</span>
          <span className="tp-blank-sub">A plain email with your logo and the footer. Add sections as you go.</span>
        </div>
        <p className="tp-card-kicker">Blank</p>
        <p className="tp-card-name">Blank email</p>
      </button>
    </div>
  )

  const savedCard = (item, kind) => {
    const trade = kind === 'mine' ? tradeOfSaved(item) : null
    return (
      <div key={`${kind}-${item.id}`} className="tp-card tp-card--saved">
        <button type="button" className="tp-card-hit" onClick={() => (kind === 'draft' ? onOpenDraft(item) : onOpenSaved(item))}>
          <div className="tp-thumb" style={{ height: thumbH }}>
            <SavedThumb blocks={item.blocks} sample={sample} height={thumbH} />
          </div>
          <p className={`tp-card-kicker ${kind === 'draft' ? 'is-draft' : 'is-yours'}`}>
            {kind === 'draft' ? 'Draft' : (view === 'mine' ? `Your template · ${tradeLabel(trade)}` : 'Your template')}
          </p>
          <p className="tp-card-name">{item.name}</p>
        </button>
        {kind === 'mine' && (
          <SavedMenu item={item} trade={trade} onSetTrade={onSetTemplateTrade} onDelete={onDeleteUserTemplate} />
        )}
      </div>
    )
  }

  const libraryCard = (tpl) => {
    const look = designedLook(tpl, own)
    return (
      <div key={tpl.id} className="tp-card">
        <button type="button" className="tp-card-hit" onClick={() => setPreviewing(tpl)}>
          <div className="tp-thumb" style={{ height: thumbH }}>
            <EmailThumb html={htmlFor(tpl)} height={thumbH} />
            <span className="tp-thumb-cta">Preview</span>
          </div>
          <p className="tp-card-kicker">{purposeLabel(tpl.purpose)}</p>
          <p className="tp-card-name">{tpl.name}</p>
          {!isMobile && <p className="tp-card-desc">{tpl.description}</p>}
          {mode === 'designed' && (
            <p className="tp-card-look"><LookDot id={look} className="tp-card-look-dot" />{LOOKS[look].name}</p>
          )}
        </button>
      </div>
    )
  }

  const pick = (id) => { hapticTick(); setView(id) }

  return (
    <div className="tp">
      {!isMobile && (
        <nav className="tp-side" aria-label="Template sections">
          <p className="tp-side-label">Templates</p>
          {views.map(n => (
            <button key={n.id} type="button" className={`tp-side-btn ${view === n.id ? 'is-on' : ''}`} onClick={() => pick(n.id)} aria-current={view === n.id ? 'true' : undefined}>
              {n.label}{n.yours && <span className="tp-side-tag">Yours</span>}
            </button>
          ))}
          {yours.length > 0 && <p className="tp-side-label tp-side-label--gap">Yours</p>}
          {yours.map(n => (
            <button key={n.id} type="button" className={`tp-side-btn ${view === n.id ? 'is-on' : ''}`} onClick={() => pick(n.id)} aria-current={view === n.id ? 'true' : undefined}>
              {n.label}<span className="tp-side-count">{n.count}</span>
            </button>
          ))}
        </nav>
      )}

      <div className="tp-main">
        <header className="tp-head">
          <div>
            <h1 className="tp-h1">Start with a template</h1>
            <p className="tp-lead">
              {mode === 'brand'
                ? 'Shown in your brand. Every word and photo is yours to change.'
                : 'Shown as they were designed. Every one works in your brand, and every word and photo is yours to change.'}
            </p>
          </div>
          {showLibrary && (
            <div className="tp-mode" role="radiogroup" aria-label="Show templates">
              <button type="button" role="radio" aria-checked={mode === 'designed'} className={mode === 'designed' ? 'is-on' : ''} onClick={() => setMode('designed')}>As designed</button>
              <button type="button" role="radio" aria-checked={mode === 'brand'} className={mode === 'brand' ? 'is-on' : ''} onClick={() => setMode('brand')}>
                <span className="tp-mode-swatch" style={{ background: brandSwatch || resolveTheme({ look: (kit && kit.look) || 'letter' }).palette.accent }} />
                In your brand
              </button>
            </div>
          )}
        </header>

        {isMobile && (
          <div className="tp-chips" role="tablist" aria-label="Template sections">
            {[...views, ...yours].map(n => (
              <button key={n.id} type="button" role="tab" aria-selected={view === n.id} className={view === n.id ? 'is-on' : ''} onClick={() => pick(n.id)}>{n.label}</button>
            ))}
          </div>
        )}

        {view === 'everything' && !isDesignMode && drafts.length > 0 && (
          <section className="tp-sec">
            <h2 className="tp-h2">Pick up where you left off</h2>
            <div className="tp-grid">{drafts.slice(0, cols).map(d => savedCard(d, 'draft'))}</div>
          </section>
        )}

        {sections.map((trade, i) => {
          const mine = savedFor(trade)
          const shownMine = view === 'everything' ? mine.slice(0, Math.max(1, cols - 1)) : mine
          return (
            <section key={trade} className="tp-sec">
              <h2 className="tp-h2">
                {TRADES[trade].title}
                {trade === own && <span className="tp-h2-note">Your business type</span>}
              </h2>
              <div className="tp-grid">
                {/* Start from scratch always leads */}
                {i === 0 && blankCard}
                {shownMine.map(t => savedCard(t, 'mine'))}
                {templatesForTrade(trade).map(libraryCard)}
              </div>
              {shownMine.length < mine.length && (
                <button type="button" className="tp-more" onClick={() => pick('mine')}>See all your templates ({userTemplates.length})</button>
              )}
            </section>
          )
        })}

        {view === 'mine' && (
          <section className="tp-sec">
            <h2 className="tp-h2">Your templates</h2>
            <div className="tp-grid">{userTemplates.map(t => savedCard(t, 'mine'))}</div>
          </section>
        )}
        {view === 'drafts' && (
          <section className="tp-sec">
            <h2 className="tp-h2">Drafts</h2>
            <div className="tp-grid">{drafts.map(d => savedCard(d, 'draft'))}</div>
          </section>
        )}
      </div>

      {previewing && (
        <TemplatePreview
          tpl={previewing}
          market={own || 'hair'}
          kit={kit}
          links={links}
          sample={sample}
          isMobile={isMobile}
          initialLook={lookFor(previewing)}
          onClose={() => setPreviewing(null)}
          onUse={(tpl, look) => { setPreviewing(null); onUseTemplate(tpl, look) }}
        />
      )}
    </div>
  )
}

export default TemplatePicker
